import React, { useState, useEffect } from 'react';
import Account from '../utils/account';
import Logger from '../../logger';
import { isSacCstAuthEnabled } from '../../feature-flags';

export interface AuthDetailsInterface {
    isAuthenticated: boolean;
    email: string;
    avatarUrl: string;
    canEdit: boolean;
    rawDetails: any;
    displayName: string;
}

const defaultDetails = {
    isAuthenticated: false,
    email: '',
    avatarUrl: '',
    canEdit: false,
    rawDetails: {},
    displayName: ''
};

// ENCAPSULATED CONTEXT
const AuthContext = React.createContext<AuthDetailsInterface>(defaultDetails);

// EXPORTED PROVIDER
export function AuthProvider(props: { children: React.ReactNode }) {
    const [details, setDetails] =
        useState<AuthDetailsInterface>(defaultDetails);

    const checkAuth: () => {} = async () => {
        try {
            const isCstFlagEnabled = await isSacCstAuthEnabled();
            const {
                isUserAuthenticated,
                getUserEmail,
                getUserAvatarUrl,
                getCanUserEdit,
                getRawDetails,
                getUserDisplayName
            } = await Account.getUserDetails(isCstFlagEnabled);

            // UPDATE STATE
            const newState = {
                isAuthenticated: await isUserAuthenticated(),
                email: await getUserEmail(),
                avatarUrl: await getUserAvatarUrl(),
                canEdit: await getCanUserEdit(),
                rawDetails: await getRawDetails(),
                displayName: await getUserDisplayName()
            };
            setDetails(newState);
        } catch (error) {
            Logger.error({ error }, 'Error getting user details');
        }
    };

    // ON MOUNT ONLY
    useEffect(() => {
        checkAuth();
    }, []);

    return (
        <AuthContext.Provider value={details}>
            {props.children}
        </AuthContext.Provider>
    );
}

// EXPORTED HOOK
export function useAuth() {
    return React.useContext(AuthContext);
}

// EXPORTED HOC
export function WithAuthDetails(WrappedComponent: React.ComponentType<any>) {
    const WrappedComponentClosure = (props: any) => (
        <AuthContext.Consumer>
            {(authDetails: AuthDetailsInterface) => (
                <WrappedComponent
                    authDetails={authDetails}
                    {...props}
                ></WrappedComponent>
            )}
        </AuthContext.Consumer>
    );

    WrappedComponentClosure.displayName = `WithAuthDetailsClosure`;
    return WrappedComponentClosure;
}
WithAuthDetails.DisplayName = 'WithAuthDetails';
